import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Plus, Trash2, SlidersHorizontal, Percent, Award, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AssessmentConfig = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/assessments/config"); setCfg(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-[#475569]">Loading…</div>;

  const weightsSum = cfg.weights.attendance + cfg.weights.ca1 + cfg.weights.ca2 + cfg.weights.exam;

  const updateBoundary = (idx, key, val) => {
    setCfg((c) => ({ ...c, grade_boundaries: c.grade_boundaries.map((b, i) => i === idx ? { ...b, [key]: key === "min" ? Number(val) : val } : b) }));
  };
  const addBoundary = () => setCfg((c) => ({ ...c, grade_boundaries: [...c.grade_boundaries, { min: 0, letter: "F", remark: "Fail" }] }));
  const removeBoundary = (idx) => setCfg((c) => ({ ...c, grade_boundaries: c.grade_boundaries.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (Math.abs(weightsSum - 100) > 0.01) return toast.error(`Weights must sum to 100 (currently ${weightsSum})`);
    setSaving(true);
    try {
      const payload = {
        weights: cfg.weights,
        attendance_rule: cfg.attendance_rule,
        grade_boundaries: cfg.grade_boundaries.sort((a, b) => b.min - a.min),
        pass_mark: cfg.pass_mark,
        ranking_method: cfg.ranking_method,
      };
      const { data } = await api.patch("/assessments/config", payload);
      setCfg(data);
      toast.success("Assessment config saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="assessment-config-page">
      <PageHeader
        overline="Configuration"
        title="Assessment Configuration"
        description="Set the weights, attendance rule, grade boundaries and ranking method. Applies immediately to all report cards and broadsheets."
        actions={
          <Button onClick={save} disabled={saving} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="ac-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save All</>}
          </Button>
        }
      />

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Weights */}
        <Card className="p-5 border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center"><Percent className="w-4 h-4" /></span>
            <div>
              <div className="font-display text-lg text-[#1B2C5A]">Assessment Weights</div>
              <div className="text-xs text-[#475569]">Must sum to 100 marks in total.</div>
            </div>
            <span className={"ml-auto text-xs px-2 py-0.5 rounded-full " + (Math.abs(weightsSum - 100) < 0.01 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>Σ = {weightsSum}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["attendance", "ca1", "ca2", "exam"].map((k) => (
              <div key={k}>
                <Label className="text-xs text-[#1B2C5A] uppercase tracking-wider">{k}</Label>
                <Input type="number" step="0.5" min="0" max="100" value={cfg.weights[k]} onChange={(e) => setCfg({ ...cfg, weights: { ...cfg.weights, [k]: Number(e.target.value) } })} className="mt-1" data-testid={`w-${k}`} />
              </div>
            ))}
          </div>
        </Card>

        {/* Attendance rule */}
        <Card className="p-5 border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center"><SlidersHorizontal className="w-4 h-4" /></span>
            <div>
              <div className="font-display text-lg text-[#1B2C5A]">Attendance Rule</div>
              <div className="text-xs text-[#475569]">How auto-calculated attendance marks are awarded.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#1B2C5A]">Mode</Label>
              <Select value={cfg.attendance_rule.mode} onValueChange={(v) => setCfg({ ...cfg, attendance_rule: { ...cfg.attendance_rule, mode: v } })}>
                <SelectTrigger className="mt-1" data-testid="att-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="threshold">Threshold (full marks if ≥ %)</SelectItem>
                  <SelectItem value="proportional">Proportional to %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#1B2C5A]">Threshold %</Label>
              <Input type="number" step="1" min="0" max="100" value={cfg.attendance_rule.threshold_pct} onChange={(e) => setCfg({ ...cfg, attendance_rule: { ...cfg.attendance_rule, threshold_pct: Number(e.target.value) } })} className="mt-1" data-testid="att-threshold" />
            </div>
          </div>
        </Card>

        {/* Pass mark + ranking */}
        <Card className="p-5 border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center"><Award className="w-4 h-4" /></span>
            <div>
              <div className="font-display text-lg text-[#1B2C5A]">Pass Mark & Ranking</div>
              <div className="text-xs text-[#475569]">Pass mark shows in reports; ranking method affects position ties.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#1B2C5A]">Pass Mark (%)</Label>
              <Input type="number" step="0.5" min="0" max="100" value={cfg.pass_mark} onChange={(e) => setCfg({ ...cfg, pass_mark: Number(e.target.value) })} className="mt-1" data-testid="passmark" />
            </div>
            <div>
              <Label className="text-xs text-[#1B2C5A]">Ranking Method</Label>
              <Select value={cfg.ranking_method} onValueChange={(v) => setCfg({ ...cfg, ranking_method: v })}>
                <SelectTrigger className="mt-1" data-testid="ranking"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (1, 2, 2, 4)</SelectItem>
                  <SelectItem value="dense">Dense (1, 2, 2, 3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Grade boundaries */}
        <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center"><Award className="w-4 h-4" /></span>
            <div>
              <div className="font-display text-lg text-[#1B2C5A]">Grade Boundaries</div>
              <div className="text-xs text-[#475569]">Higher <em>min %</em> first. e.g., A1 → 75+, B2 → 70+, etc.</div>
            </div>
            <Button size="sm" onClick={addBoundary} variant="outline" className="ml-auto border-[#9DCD3F]" data-testid="add-boundary"><Plus className="w-3.5 h-3.5 mr-1" /> Add Grade</Button>
          </div>
          <div className="space-y-2">
            {cfg.grade_boundaries.map((b, idx) => (
              <div key={idx} className="grid grid-cols-[80px_100px_1fr_40px] gap-2 items-center" data-testid={`gb-row-${idx}`}>
                <Input type="number" min="0" max="100" value={b.min} onChange={(e) => updateBoundary(idx, "min", e.target.value)} data-testid={`gb-min-${idx}`} />
                <Input value={b.letter} onChange={(e) => updateBoundary(idx, "letter", e.target.value)} placeholder="A1" data-testid={`gb-letter-${idx}`} />
                <Input value={b.remark} onChange={(e) => updateBoundary(idx, "remark", e.target.value)} placeholder="Excellent" data-testid={`gb-remark-${idx}`} />
                <Button size="icon" variant="ghost" onClick={() => removeBoundary(idx)} className="text-red-700" data-testid={`gb-del-${idx}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AssessmentConfig;
